import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PDF Viewer Component
 *
 * High-performance PDF viewer using react-pdf (PDF.js).
 * Features:
 * - Page navigation
 * - Zoom controls
 * - Download button
 * - Full-screen modal
 *
 * THEMING: Uses CSS variables for colors, allowing each app to customize.
 * Required CSS variables (with defaults):
 *   --pdf-bg-dark: #1a1a2e
 *   --pdf-bg-darker: #0f0f1a
 *   --pdf-primary: #6c8cff
 *   --pdf-error: #f87171
 *
 * @example
 * ```tsx
 * import { PDFViewer } from '@boosting/ui-kit';
 *
 * <PDFViewer
 *   isOpen={showPdf}
 *   onClose={() => setShowPdf(false)}
 *   pdfUrl="https://storage.googleapis.com/bucket/file.pdf"
 *   pdfName="document.pdf"
 * />
 * ```
 */
import { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { X, Download, ExternalLink, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
// IMPORTANT: Configure PDF.js worker AFTER importing react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
// Default theme - can be overridden via CSS variables or props
const DEFAULT_THEME = {
    bgDark: 'var(--pdf-bg-dark, #1f2937)',
    bgDarker: 'var(--pdf-bg-darker, #111827)',
    primary: 'var(--pdf-primary, #3b82f6)',
    error: 'var(--pdf-error, #ef4444)',
    textMuted: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(255, 255, 255, 0.1)',
};
export function PDFViewer({ isOpen, onClose, pdfUrl, pdfName, workerUrl, theme }) {
    const [numPages, setNumPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Merge theme with defaults
    const colors = { ...DEFAULT_THEME, ...theme };
    // Set custom worker URL if provided
    useEffect(() => {
        if (workerUrl) {
            pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        }
    }, [workerUrl]);
    const onDocumentLoadSuccess = useCallback(({ numPages }) => {
        setNumPages(numPages);
        setLoading(false);
        setError(null);
    }, []);
    const onDocumentLoadError = useCallback((error) => {
        setLoading(false);
        setError(`Error loading PDF: ${error.message}`);
    }, []);
    const goToPrevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
    const goToNextPage = () => setPageNumber((prev) => Math.min(prev + 1, numPages));
    const zoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3));
    const zoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));
    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = pdfName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleOpenNewTab = () => {
        window.open(pdfUrl, '_blank');
    };
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    // Handle escape key and body scroll lock
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);
    // Reset state when PDF changes
    useEffect(() => {
        if (isOpen) {
            setPageNumber(1);
            setScale(1.0);
            setLoading(true);
            setError(null);
        }
    }, [pdfUrl, isOpen]);
    if (!isOpen)
        return null;
    return (_jsx("div", { className: "pdf-viewer-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/85", onClick: handleBackdropClick, children: _jsxs("div", { className: "pdf-viewer-modal relative w-full max-w-5xl h-[95vh] rounded-xl overflow-hidden shadow-2xl flex flex-col", style: { backgroundColor: colors.bgDark }, children: [_jsxs("div", { className: "pdf-viewer-header flex items-center justify-between px-4 py-3 shrink-0", style: {
                        borderBottom: `1px solid ${colors.border}`,
                        background: colors.bgDark,
                    }, children: [_jsxs("div", { className: "flex items-center gap-3 min-w-0", children: [_jsx("span", { className: "font-medium text-white text-sm truncate", children: pdfName }), numPages > 0 && (_jsxs("span", { className: "text-xs px-2 py-0.5 rounded-full", style: {
                                        background: `color-mix(in srgb, ${colors.primary} 20%, transparent)`,
                                        color: colors.primary
                                    }, children: [numPages, " p\u00E1ginas"] }))] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("button", { onClick: zoomOut, className: "p-2 rounded-lg hover:bg-white/10 transition-colors", style: { color: colors.textMuted }, title: "Reducir zoom", children: _jsx(ZoomOut, { className: "h-4 w-4" }) }), _jsxs("span", { className: "text-xs min-w-[40px] text-center", style: { color: colors.textMuted }, children: [Math.round(scale * 100), "%"] }), _jsx("button", { onClick: zoomIn, className: "p-2 rounded-lg hover:bg-white/10 transition-colors", style: { color: colors.textMuted }, title: "Aumentar zoom", children: _jsx(ZoomIn, { className: "h-4 w-4" }) }), _jsx("div", { className: "w-px h-5 mx-2", style: { backgroundColor: colors.border } }), _jsxs("button", { onClick: handleDownload, className: "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium hover:opacity-80 transition-opacity", style: {
                                        background: `color-mix(in srgb, ${colors.primary} 15%, transparent)`,
                                        color: colors.primary
                                    }, title: "Descargar PDF", children: [_jsx(Download, { className: "h-3.5 w-3.5" }), "Descargar"] }), _jsx("button", { onClick: handleOpenNewTab, className: "p-2 rounded-lg hover:bg-white/10 transition-colors", style: { color: colors.textMuted }, title: "Abrir en nueva pesta\u00F1a", children: _jsx(ExternalLink, { className: "h-4 w-4" }) }), _jsx("button", { onClick: onClose, className: "p-2 rounded-lg hover:bg-white/10 transition-colors ml-2", style: { color: colors.textMuted }, title: "Cerrar", children: _jsx(X, { className: "h-5 w-5" }) })] })] }), _jsxs("div", { className: "pdf-viewer-content flex-1 overflow-auto flex items-start justify-center p-4", style: { background: colors.bgDarker }, children: [loading && (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx(Loader2, { className: "h-8 w-8 animate-spin", style: { color: colors.primary } }) })), error && (_jsxs("div", { className: "flex flex-col items-center justify-center h-full text-center p-8", children: [_jsx("p", { className: "mb-4", style: { color: colors.error }, children: error }), _jsxs("button", { onClick: handleOpenNewTab, className: "flex items-center gap-2 px-4 py-2 rounded-lg text-sm hover:opacity-80 transition-opacity", style: {
                                        background: `color-mix(in srgb, ${colors.primary} 20%, transparent)`,
                                        color: colors.primary
                                    }, children: [_jsx(ExternalLink, { className: "h-4 w-4" }), "Abrir en nueva pesta\u00F1a"] })] })), _jsx(Document, { file: pdfUrl, onLoadSuccess: onDocumentLoadSuccess, onLoadError: onDocumentLoadError, loading: "", children: _jsx(Page, { pageNumber: pageNumber, scale: scale, renderTextLayer: false, renderAnnotationLayer: false, loading: "" }) })] }), numPages > 1 && (_jsxs("div", { className: "pdf-viewer-footer flex items-center justify-center gap-4 px-4 py-3 shrink-0", style: {
                        borderTop: `1px solid ${colors.border}`,
                        background: colors.bgDark,
                    }, children: [_jsx("button", { onClick: goToPrevPage, disabled: pageNumber <= 1, className: "p-2 rounded-lg transition-opacity disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10", style: { color: 'white' }, children: _jsx(ChevronLeft, { className: "h-5 w-5" }) }), _jsxs("span", { className: "text-white text-sm", children: ["P\u00E1gina ", _jsx("strong", { children: pageNumber }), " de ", _jsx("strong", { children: numPages })] }), _jsx("button", { onClick: goToNextPage, disabled: pageNumber >= numPages, className: "p-2 rounded-lg transition-opacity disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10", style: { color: 'white' }, children: _jsx(ChevronRight, { className: "h-5 w-5" }) })] }))] }) }));
}
export default PDFViewer;
